import { inject, Injectable, signal } from '@angular/core';
import { SupabaseService } from '../auth/supabase.service';

export interface AccessibleClinic {
  readonly id: number;
  readonly name: string;
}

const STORAGE_KEY = 'metasmile.activeClinicId';

/**
 * Clinic switching is OFF.
 *
 * `my_accessible_clinics()` already returns every clinic a user holds a role at
 * (and all of them for platform-role holders), but the data layer does not follow:
 * `crm.current_branch_id()` resolves to `users_tb.branches_id` alone and is used by
 * ~77 RLS policies. Switching therefore offered clinics whose rows RLS would never
 * return — screens would come back empty, or worse show the home clinic's rows under
 * another clinic's name, and inserts stamped with the selected `branches_id` would be
 * rejected by the policies' WITH CHECK.
 *
 * Flip this back to `true` once RLS honours per-clinic grants; the switcher UI and
 * `setActiveClinic()` are intact behind it.
 */
export const MULTI_CLINIC_SWITCHING_ENABLED = false;

/**
 * Single source of truth for "which clinic is the CRM app currently showing
 * data for" — global, header-driven, replacing the old page-local switcher
 * that only lived inside Settings. A single-clinic user always has exactly
 * one accessible clinic (their own); a multi-clinic/platform-wide grant
 * holder gets more, and picks among them here.
 */
@Injectable({ providedIn: 'root' })
export class ActiveClinicService {
  private readonly supabase = inject(SupabaseService).client;

  private readonly _accessibleClinics = signal<readonly AccessibleClinic[]>([]);
  readonly accessibleClinics = this._accessibleClinics.asReadonly();

  private readonly _activeClinicId = signal<number | null>(null);
  readonly activeClinicId = this._activeClinicId.asReadonly();

  async refresh(homeClinicId: number | null): Promise<void> {
    const { data, error } = await this.supabase.schema('crm').rpc('my_accessible_clinics');

    const clinics: AccessibleClinic[] =
      error || !data
        ? []
        : (data as any[]).map((row) => ({ id: row.branches_id, name: row.branch_name }));

    this._accessibleClinics.set(clinics);
    this._activeClinicId.set(this.resolveDefault(clinics, homeClinicId));
  }

  setActiveClinic(id: number): void {
    // Guarded rather than removed: with switching off, nothing may move the active
    // clinic away from the user's home clinic, whatever calls this.
    if (!MULTI_CLINIC_SWITCHING_ENABLED) {
      return;
    }

    this._activeClinicId.set(id);
    try {
      localStorage.setItem(STORAGE_KEY, String(id));
    } catch {
      // localStorage can throw in private-browsing/SSR-like contexts; the in-memory signal still works.
    }
  }

  clear(): void {
    this._accessibleClinics.set([]);
    this._activeClinicId.set(null);
  }

  private resolveDefault(clinics: readonly AccessibleClinic[], homeClinicId: number | null): number | null {
    if (clinics.length === 0) {
      return null;
    }

    // With switching off, a previously stored selection must not survive: a user who
    // switched while it was enabled would otherwise stay pinned to a foreign clinic
    // with no visible control to get back. Drop the key and fall through to home.
    if (!MULTI_CLINIC_SWITCHING_ENABLED) {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        // Private-browsing/SSR contexts can throw; the in-memory signal is still correct.
      }
      return homeClinicId ?? clinics[0].id;
    }

    let stored: number | null = null;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      stored = raw ? Number(raw) : null;
    } catch {
      stored = null;
    }

    if (stored !== null && clinics.some((c) => c.id === stored)) {
      return stored;
    }

    if (homeClinicId !== null && clinics.some((c) => c.id === homeClinicId)) {
      return homeClinicId;
    }

    return clinics[0].id;
  }
}
